import type { InferOutput } from 'valibot'
import { getSolrDocumentAuthor, getSolrDocumentDate, noticeTypeToLabel, resolveSolrDocumentUrl } from '#shared/utils/solr'
import { intersect, nullish, object, pick, pipe, string, transform } from 'valibot'
import { baseCommentarySchema } from './commentary'
import { normalizeJuridikaImageUrl } from './juridika-media-image'
import { transformShortTitle } from './law-utils'
import { createSolrResponseSchema } from './schemas'
import { globalSearchSchema } from '#shared/utils/solr-search'

/**
 * Schema for newest documents combining all 5 document types:
 * - legal_commentary
 * - book
 * - academic_article
 * - journal_article
 * - journal_editorial
 */
export const newestDocSchema = pipe(
  intersect([
    pick(globalSearchSchema, [
      'id',
      'shortTitle',
      'nodeType',
      'volume',
      'issue',
      'author',
      'author_list',
      'authorName',
      'publish_date',
      'lastUpdatedByEditor',
      'dateOfPublication',
      'year',
      'editionNumber',
      'coverImage',
      'bodytext',
      'path_location',
      'journalId',
      'lawId',
      'lawFragmentId',
      'remoteId',
      'publisherId',
      'ezMainNodeId',
    ]),
    pick(baseCommentarySchema, [
      'originalPublicationDate',
      'editionId',
    ]),
    object({
      title: nullish(string()),
      journalTitle: nullish(string()),
      noticeType: nullish(string()),
    }),
  ]),
  transform((doc) => {
    const nodeType = doc.nodeType
    const transformedTitle = transformShortTitle(doc.shortTitle).title
    const authors = getSolrDocumentAuthor(doc)
    const date = getSolrDocumentDate(doc)
    const url = resolveSolrDocumentUrl(doc)
    const normalizedBodytext = normalizeSolrRichText(doc.bodytext || '')
    const coverImage = normalizeJuridikaImageUrl(doc.coverImage)
    const noticeType = noticeTypeToLabel(doc.noticeType || '')

    const baseData = {
      id: doc.id,
      authors,
      nodeType,
      date,
      title: doc.nodeType === 'legal_commentary' ? transformedTitle : doc.title,
    }

    return {
      ...baseData,
      editionId: doc.editionId ?? undefined,
      editionNumber: doc.editionNumber ?? undefined,
      bodytext: normalizedBodytext ?? undefined,
      coverImage: coverImage ?? undefined,
      volume: doc.volume ?? undefined,
      issue: doc.issue ?? undefined,
      remoteId: doc.remoteId ?? undefined,
      journalId: doc.journalId ?? undefined,
      publisherId: doc.publisherId ?? undefined,
      lawId: doc.lawId ?? undefined,
      lawFragmentId: doc.lawFragmentId ?? undefined,
      subTitle: doc.nodeType === 'legal_commentary'
        ? doc.title
        : (doc.journalTitle || undefined),
      url: url ?? undefined,
      ezMainNodeId: doc.ezMainNodeId,
      noticeType,
    }
  }),
)

export type NewestDoc = InferOutput<typeof newestDocSchema>

export const newestDocResponseSchema = createSolrResponseSchema(newestDocSchema)

export type NewestDocResponseSchema = InferOutput<typeof newestDocResponseSchema>
